import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getHeadersWithUniqueVisitorId,
  loadConversation,
} from "@brandclub/common-ui";
import { ConversationLoaderData } from "../types/conversation";
import { processConversationData } from "../utils/conversationProcessor";

interface UseConversationLoaderProps {
  conversationId: string | null;
  onConversationLoaded: (
    messages: ReturnType<typeof processConversationData>["messages"],
    productMap: ReturnType<typeof processConversationData>["productMap"],
    followUpQuestions: ReturnType<typeof processConversationData>["followUpQuestions"],
    previewSearchQueries: ReturnType<typeof processConversationData>["previewSearchQueries"],
    previewQueryProducts: ReturnType<typeof processConversationData>["previewQueryProducts"]
  ) => void;
  onConversationNotFound: () => void;
  isActivelyStreaming?: boolean;
  conversationHistoryLength?: number;
}

export const useConversationLoader = ({
  conversationId,
  onConversationLoaded,
  onConversationNotFound,
  isActivelyStreaming = false,
  conversationHistoryLength = 0,
}: UseConversationLoaderProps) => {
  const queryClient = useQueryClient();
  const loadedConversationIdRef = useRef<string | null>(null);
  const [forceReload, setForceReload] = useState(0);
  const previousConversationIdRef = useRef<string | null>(null);
  const wasStreamingRef = useRef(false);

  // Track streaming state transitions
  useEffect(() => {
    wasStreamingRef.current = isActivelyStreaming;
  }, [isActivelyStreaming]);

  // Allow loading when conversationId changes (e.g., from sidebar selection)
  useEffect(() => {
    const prevId = previousConversationIdRef.current;
    previousConversationIdRef.current = conversationId;

    // Don't force reload if we're actively streaming
    if (isActivelyStreaming) {
      // Just mark as loaded to prevent future loads during this streaming session
      if (conversationId) {
        loadedConversationIdRef.current = conversationId;
      }
      return;
    }

    // If we just finished streaming, don't reload - we already have the data
    if (wasStreamingRef.current && !isActivelyStreaming) {
      if (conversationId) {
        loadedConversationIdRef.current = conversationId;
      }
      return;
    }

    if (conversationId && conversationId !== loadedConversationIdRef.current) {
      // Check if the conversation is already loaded in the context
      // This happens when navigating from widget to full page
      if (conversationHistoryLength > 0) {
        // Mark as loaded without triggering a query
        loadedConversationIdRef.current = conversationId;
        return;
      }

      // Force reload by incrementing state
      setForceReload((prev) => prev + 1);
      // Invalidate any cached conversation data to ensure fresh load
      queryClient.invalidateQueries({
        queryKey: ["conversation", conversationId],
      });
    } else if (
      conversationId &&
      prevId === null &&
      conversationId === loadedConversationIdRef.current
    ) {
      // Special case: conversationId was null and now matches the already-loaded conversation
      // This happens when clicking the same conversation - force a reload
      setForceReload((prev) => prev + 1);
      queryClient.invalidateQueries({
        queryKey: ["conversation", conversationId],
      });
    }
  }, [conversationId, queryClient, isActivelyStreaming, conversationHistoryLength]);

  // Use React Query to load conversation
  const {
    data: loadedConversationData,
    isLoading: isLoadingConversationQuery,
    error: conversationLoadError,
  } = useQuery<ConversationLoaderData | null>({
    queryKey: ["conversation", conversationId, forceReload],
    queryFn: async () => {
      if (!conversationId) {
        return null;
      }
      const headers = await getHeadersWithUniqueVisitorId();
      return loadConversation({
        conversationId,
        headersToInject: headers,
      });
    },
    enabled:
      !!conversationId &&
      !isActivelyStreaming &&
      conversationId !== loadedConversationIdRef.current,
    retry: false,
    staleTime: Infinity,
  });

  // Process loaded conversation data
  useEffect(() => {
    if (loadedConversationData && conversationId) {
      // Check if user is unauthorized to view this conversation
      if ((loadedConversationData as any).error === 'unauthorized') {
        console.warn(`Unauthorized to access conversation ${conversationId}, redirecting`);
        onConversationNotFound();
        loadedConversationIdRef.current = conversationId;
        // Optionally navigate to concierge home
        if (typeof window !== 'undefined') {
          window.history.pushState({}, '', '/concierge');
        }
        return;
      }

      const {
        messages: processedMessages,
        productMap,
        followUpQuestions,
        previewSearchQueries,
        previewQueryProducts,
      } = processConversationData(loadedConversationData, conversationId);

      if (processedMessages.length > 0) {
        onConversationLoaded(
          processedMessages,
          productMap,
          followUpQuestions,
          previewSearchQueries,
          previewQueryProducts
        );
        loadedConversationIdRef.current = conversationId;
      } else {
        // Conversation not found - notify parent
        console.warn(`Conversation ${conversationId} not found, clearing URL`);
        onConversationNotFound();
        loadedConversationIdRef.current = conversationId;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedConversationData, conversationId]);

  // Handle conversation load errors
  useEffect(() => {
    if (conversationLoadError && conversationId) {
      console.error("Failed to load conversation:", conversationLoadError);
      onConversationNotFound();
      loadedConversationIdRef.current = conversationId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationLoadError, conversationId]);

  return {
    isLoadingConversationQuery,
    conversationLoadError,
  };
};
